import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Image as ImageIcon, Download, RefreshCw, Wand2, X, Sparkles, Zap, Move, Droplet, Share2, Star, Send, Instagram, Facebook } from "lucide-react";
import SocialImageDesigner from "@/components/admin/SocialImageDesigner";
import SocialImageShareModal from "@/components/admin/SocialImageShareModal";
import SocialPublishModal from "@/components/admin/SocialPublishModal";
import SocialBatchPublishModal from "@/components/admin/SocialBatchPublishModal";

/**
 * AdminSocialImagesPage — "Editor de fotos" / "Generador para redes".
 * Lista las noticias publicadas más recientes y permite generar Post
 * (1080×1350) e Historia (1080×1920) con la plantilla "Sur Primicia Clásica".
 */
export default function AdminSocialImagesPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [generating, setGenerating] = useState(new Set());
  const [imagesByArticle, setImagesByArticle] = useState({}); // article_id -> [images]
  const [previewImage, setPreviewImage] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  const [template, setTemplate] = useState("surprimicia-clasica");
  const [watermarkBatch, setWatermarkBatch] = useState(false);
  const [designerArticle, setDesignerArticle] = useState(null);
  const [defaultPreset, setDefaultPreset] = useState(null);
  const [shareImage, setShareImage] = useState(null);
  const [publishTarget, setPublishTarget] = useState(null); // {image, article, platforms?}
  const [batchPublishItems, setBatchPublishItems] = useState(null); // [{image, article}]

  useEffect(() => {
    (async () => {
      try {
        const res = await api.adminListArticles({ status: "published", limit: 60 });
        const items = res?.items || res || [];
        setArticles(Array.isArray(items) ? items : []);
        // Fetch existing social images
        const soc = await api.adminSocialImagesList({ limit: 200 });
        const byArt = {};
        (soc.items || []).forEach((i) => {
          (byArt[i.article_id] = byArt[i.article_id] || []).push(i);
        });
        setImagesByArticle(byArt);
        // Fetch default preset (if any)
        try {
          const def = await api.adminSocialPresetDefault();
          setDefaultPreset(def?.preset || null);
        } catch { /* silent */ }
      } catch (e) {
        toast.error("No se pudieron cargar las noticias.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter((a) => (a.title || "").toLowerCase().includes(q)
      || (a.main_category_name || "").toLowerCase().includes(q)
      || (a.city || "").toLowerCase().includes(q));
  }, [articles, query]);

  const toggle = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const generateOne = async (articleId, overrides = {}) => {
    setGenerating((g) => new Set([...g, articleId]));
    try {
      const p = defaultPreset;
      const results = [];
      if (p) {
        // Per-format layouts: call once per format
        for (const fmt of ["post", "story"]) {
          const layoutFmt = fmt === "post" ? p.layout_post : p.layout_story;
          const res = await api.adminSocialImagesGenerate({
            article_id: articleId,
            formats: [fmt],
            template_id: overrides.template_id || p.template_id || template,
            title_override: overrides.title_override || null,
            watermark: !!p.watermark,
            watermark_opacity: p.watermark ? p.watermark_opacity : null,
            watermark_scale: p.watermark ? p.watermark_scale : null,
            logo_scale: p.logo_scale ?? null,
            layout: layoutFmt || null,
          });
          results.push(...(res.outputs || []));
          if (res.errors?.length) toast.warning(`${res.errors.length} formato(s) con error`);
        }
      } else {
        const res = await api.adminSocialImagesGenerate({
          article_id: articleId,
          formats: ["post", "story"],
          template_id: overrides.template_id || template,
          title_override: overrides.title_override || null,
          watermark: watermarkBatch,
        });
        results.push(...(res.outputs || []));
        if (res.errors?.length) toast.warning(`${res.errors.length} formato(s) con error`);
      }
      if (results.length) toast.success(`${results.length} imagen(es) generada(s)${p ? ` con preset "${p.name}"` : ""}`);
      setImagesByArticle((prev) => ({ ...prev, [articleId]: [...results, ...(prev[articleId] || [])] }));
    } catch (e) {
      toast.error("Error generando imágenes.");
    } finally {
      setGenerating((g) => { const s = new Set(g); s.delete(articleId); return s; });
    }
  };

  const shortenAndGenerate = async (article) => {
    const orig = article.title || "";
    toast.info("Pidiéndole a la IA un título corto…");
    try {
      const res = await api.adminSocialShortenTitle({ title: orig, max_chars: 70 });
      const suggested = res.short || orig;
      if (suggested === orig || !res.used_ai) {
        toast.info("La IA no acortó el título; usaremos el original.");
        return generateOne(article.id);
      }
      const use = window.confirm(`Título original (${orig.length}):\n${orig}\n\nTítulo sugerido (${suggested.length}):\n${suggested}\n\n¿Usar el sugerido para la placa?`);
      return generateOne(article.id, { title_override: use ? suggested : null });
    } catch {
      toast.error("Error consultando la IA. Usaremos el título original.");
      return generateOne(article.id);
    }
  };

  const generateBatch = async () => {
    if (selected.size === 0) return;
    const ids = [...selected];
    setBatchRunning(true);
    setBatchProgress({ done: 0, total: ids.length * 2 });
    try {
      const p = defaultPreset;
      const batchArgs = {
        article_ids: ids,
        formats: ["post", "story"],
        template_id: p?.template_id || template,
        watermark: p ? !!p.watermark : watermarkBatch,
        watermark_opacity: p?.watermark ? p.watermark_opacity : null,
        watermark_scale: p?.watermark ? p.watermark_scale : null,
        logo_scale: p?.logo_scale ?? null,
        // Per-format layouts from the default preset (post + story separate)
        layout_post: p?.layout_post || null,
        layout_story: p?.layout_story || null,
      };
      const { job_id, total } = await api.adminSocialImagesBatchGenerate(batchArgs);
      setBatchProgress({ done: 0, total });
      // Poll every 1.5s
      let stopped = false;
      const seenOutputIds = new Set();
      while (!stopped) {
        await new Promise((r) => setTimeout(r, 1500));
        let job;
        try {
          job = await api.adminSocialImagesJobStatus(job_id);
        } catch {
          continue;
        }
        setBatchProgress({ done: (job.done || 0) + (job.failed || 0), total: job.total || total });
        // Merge new outputs into the UI as they arrive
        const outs = job.outputs || [];
        if (outs.length) {
          setImagesByArticle((prev) => {
            const next = { ...prev };
            for (const im of outs) {
              if (seenOutputIds.has(im.id)) continue;
              seenOutputIds.add(im.id);
              next[im.article_id] = [im, ...(next[im.article_id] || [])];
            }
            return next;
          });
        }
        if (job.status === "completed") {
          stopped = true;
          if ((job.failed || 0) > 0) {
            toast.warning(`Lote completado con ${job.failed} error(es).`);
          } else {
            toast.success(`Lote completado: ${job.done} imágenes generadas.`);
          }
        }
      }
    } catch (e) {
      toast.error("Error iniciando el lote.");
    } finally {
      setBatchRunning(false);
      setSelected(new Set());
    }
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "descarga.jpg";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const extractFilename = (resp, fallback) => {
    const cd = resp?.headers?.["content-disposition"] || resp?.headers?.get?.("content-disposition") || "";
    const m = /filename="([^"]+)"/i.exec(cd);
    return m ? m[1] : fallback;
  };

  const downloadImage = async (im) => {
    try {
      const resp = await api.adminSocialImageDownload(im.id);
      const filename = extractFilename(resp, `${im.format || "post"}-placa.jpg`);
      triggerBlobDownload(resp.data, filename);
      toast.success("Descarga iniciada");
    } catch {
      toast.error("No se pudo descargar la imagen. Reintentar.");
    }
  };

  const downloadPair = async (articleId) => {
    const imgs = imagesByArticle[articleId] || [];
    // Prefer the most recent post + story
    const post = imgs.find((i) => i.format === "post");
    const story = imgs.find((i) => i.format === "story");
    const picked = [post, story].filter(Boolean);
    if (picked.length === 0) { toast.error("No hay imágenes generadas"); return; }
    if (picked.length === 1) { await downloadImage(picked[0]); return; }
    try {
      const resp = await api.adminSocialImagesDownloadBundle(picked.map((i) => i.id));
      const filename = extractFilename(resp, `surprimicia-placas.zip`);
      triggerBlobDownload(resp.data, filename);
      toast.success("ZIP con POST + STORY descargado");
    } catch {
      toast.error("No se pudo generar el ZIP");
    }
  };

  const downloadSelectedBundle = async () => {
    // Collect the most recent post + story per selected article
    const ids = [];
    for (const aid of selected) {
      const imgs = imagesByArticle[aid] || [];
      const post = imgs.find((i) => i.format === "post");
      const story = imgs.find((i) => i.format === "story");
      if (post) ids.push(post.id);
      if (story) ids.push(story.id);
    }
    if (ids.length === 0) {
      toast.error("Ninguna imagen encontrada en la selección");
      return;
    }
    try {
      const resp = await api.adminSocialImagesDownloadBundle(ids);
      const filename = extractFilename(resp, "surprimicia-social.zip");
      triggerBlobDownload(resp.data, filename);
      toast.success(`ZIP con ${ids.length} imagen(es) descargado`);
    } catch {
      toast.error("No se pudo generar el ZIP");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-social-images-page">
      {defaultPreset && (
        <div
          data-testid="default-preset-banner"
          className="flex items-center justify-between gap-3 bg-turquoise/10 border border-turquoise/40 px-4 py-2.5"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Star className="w-4 h-4 text-turquoise fill-turquoise flex-shrink-0" />
            <span className="text-sm text-nightblue">
              Preset predeterminado activo: <b className="text-blueblack">{defaultPreset.name}</b> — se aplicará automáticamente a cada nueva placa.
            </span>
          </div>
          <button
            data-testid="default-preset-clear"
            onClick={async () => {
              try {
                await api.adminSocialPresetClearDefault();
                setDefaultPreset(null);
                toast.success("Preset predeterminado desactivado");
              } catch { toast.error("Error"); }
            }}
            className="text-[11px] uppercase tracking-widest font-bold text-nightblue hover:text-turquoise flex-shrink-0"
          >
            Quitar
          </button>
        </div>
      )}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-bold">Redes sociales</div>
          <h1 className="font-serif text-3xl font-black text-blueblack">Editor de fotos</h1>
          <p className="text-sm text-textgray mt-1">Generá Post (1080×1350) e Historia (1080×1920) para Instagram desde tus noticias publicadas.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 border border-softblue" data-testid="template-picker">
            {[
              { key: "surprimicia-clasica", label: "Clásica", icon: null },
              { key: "breaking", label: "Breaking", icon: Zap },
            ].map((t) => {
              const Icon = t.icon;
              const active = template === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`template-${t.key}`}
                  onClick={() => setTemplate(t.key)}
                  className={`px-3 h-10 text-[11px] uppercase tracking-widest font-bold inline-flex items-center gap-1.5 ${active ? "bg-nightblue text-white" : "bg-white text-nightblue hover:bg-lightgray"}`}
                >
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  {t.label}
                </button>
              );
            })}
          </div>
          <Input
            data-testid="social-images-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por título, categoría o ciudad…"
            className="h-10 w-72 bg-lightgray border-softblue"
          />
          <label className="flex items-center gap-2 cursor-pointer h-10 px-3 border border-softblue bg-white">
            <input
              type="checkbox"
              data-testid="social-images-watermark-toggle"
              checked={watermarkBatch}
              onChange={(e) => setWatermarkBatch(e.target.checked)}
              className="w-4 h-4 accent-turquoise"
            />
            <Droplet className="w-3.5 h-3.5 text-turquoise" />
            <span className="text-[11px] uppercase tracking-widest font-bold text-nightblue">Marca de agua</span>
          </label>
          <Button
            data-testid="social-images-batch"
            disabled={selected.size === 0 || batchRunning}
            onClick={generateBatch}
            className="h-10 bg-nightblue hover:bg-blueblack text-white rounded-none font-semibold uppercase text-xs tracking-widest"
          >
            {batchRunning ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {batchProgress.done}/{batchProgress.total}</>
            ) : (
              <><Wand2 className="w-4 h-4 mr-2" /> Generar seleccionadas ({selected.size})</>
            )}
          </Button>
          <Button
            data-testid="social-images-download-selected"
            disabled={selected.size === 0}
            onClick={downloadSelectedBundle}
            variant="outline"
            className="h-10 border-softblue text-nightblue hover:bg-lightgray rounded-none font-semibold uppercase text-xs tracking-widest"
            title="Descargar todas las placas de las noticias seleccionadas en un ZIP"
          >
            <Download className="w-4 h-4 mr-2" /> Descargar lote ({selected.size})
          </Button>
          <Button
            data-testid="social-images-publish-selected"
            disabled={selected.size === 0}
            onClick={() => {
              const chosen = [];
              selected.forEach((aid) => {
                const article = articles.find((x) => x.id === aid);
                const imgs = imagesByArticle[aid] || [];
                // Prefer the newest POST for feed publishing
                const post = imgs.find((i) => i.format === "post") || imgs[0];
                if (article && post) chosen.push({ image: post, article });
              });
              if (!chosen.length) {
                toast.error("Ninguna de las noticias seleccionadas tiene placa generada.");
                return;
              }
              setBatchPublishItems(chosen);
            }}
            className="h-10 bg-turquoise hover:bg-nightblue text-white rounded-none font-semibold uppercase text-xs tracking-widest"
            title="Publicar en redes las placas de todas las noticias seleccionadas"
          >
            <Send className="w-4 h-4 mr-2" /> Publicar seleccionadas ({selected.size})
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-textgray">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-textgray">No hay noticias publicadas.</div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((a) => {
            const imgs = imagesByArticle[a.id] || [];
            const gen = generating.has(a.id);
            const chosen = selected.has(a.id);
            return (
              <div
                key={a.id}
                data-testid={`social-images-row-${a.id}`}
                className={`bg-white border ${chosen ? "border-turquoise" : "border-softblue"} p-3 flex items-center gap-4`}
              >
                <input
                  type="checkbox"
                  checked={chosen}
                  onChange={() => toggle(a.id)}
                  data-testid={`social-images-check-${a.id}`}
                  className="w-4 h-4 accent-turquoise"
                />
                <div className="w-24 h-24 bg-lightgray overflow-hidden flex-shrink-0">
                  {a.main_image && (
                    <img src={a.main_image} alt={a.title} className="w-full h-full object-cover" style={{ objectPosition: a.image_focal_point || "50% 40%" }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-turquoise font-bold">{a.main_category_name || "Sin categoría"} {a.city ? `· ${a.city}` : ""}</div>
                  <h3 className="font-serif font-bold text-blueblack text-lg leading-snug line-clamp-2">{a.title}</h3>
                  <div className="text-[11px] text-textgray mt-1">
                    {a.published_at ? new Date(a.published_at).toLocaleString("es-AR") : "—"}
                    {imgs.length > 0 && <span className="ml-3 text-emerald-700 font-semibold">✓ {imgs.length} imagen(es) generada(s)</span>}
                  </div>
                  {imgs.length > 0 && (
                    <div className="mt-2 flex gap-2 flex-wrap items-center">
                      {imgs.slice(0, 6).map((im) => (
                        <div key={im.id} className="flex items-center gap-1 border border-softblue text-[11px]">
                          <button
                            type="button"
                            onClick={() => setPreviewImage(im)}
                            className="px-2 py-1 uppercase tracking-widest font-bold text-nightblue hover:bg-lightgray"
                          >
                            {im.format}
                          </button>
                          <button
                            type="button"
                            onClick={() => setPublishTarget({ image: im, article: a, platforms: ["instagram"] })}
                            className="px-2 py-1 border-l border-softblue text-pink-600 hover:bg-pink-50"
                            title={im.format === "story" ? "Publicar Story en Instagram" : "Publicar en Instagram"}
                            data-testid={`social-images-publish-ig-${im.id}`}
                          >
                            <Instagram className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPublishTarget({ image: im, article: a, platforms: ["facebook"] })}
                            className="px-2 py-1 border-l border-softblue text-[#1877F2] hover:bg-blue-50"
                            title="Publicar en Facebook"
                            data-testid={`social-images-publish-fb-${im.id}`}
                          >
                            <Facebook className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPublishTarget({ image: im, article: a, platforms: ["instagram", "facebook"] })}
                            className="px-2 py-1 border-l border-softblue text-nightblue hover:bg-lightgray"
                            title="Publicar en ambas"
                            data-testid={`social-images-publish-both-${im.id}`}
                          >
                            <Send className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setShareImage(im)}
                            className="px-2 py-1 border-l border-softblue text-nightblue hover:bg-lightgray"
                            title="Compartir por WhatsApp / Telegram"
                            data-testid={`social-images-share-${im.id}`}
                          >
                            <Share2 className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => downloadImage(im)}
                            className="px-2 py-1 border-l border-softblue text-turquoise hover:bg-lightgray"
                            title="Descargar"
                            data-testid={`social-images-download-${im.id}`}
                          >
                            <Download className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {imgs.some((i) => i.format === "post") && imgs.some((i) => i.format === "story") && (
                        <button
                          type="button"
                          onClick={() => downloadPair(a.id)}
                          data-testid={`social-images-download-pair-${a.id}`}
                          className="h-7 px-2 border border-turquoise text-turquoise hover:bg-turquoise/10 text-[10px] uppercase tracking-widest font-bold"
                          title="Descargar POST + STORY como ZIP"
                        >
                          <Download className="w-3 h-3 inline mr-1" /> Ambas
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <Button
                    data-testid={`social-images-generate-${a.id}`}
                    onClick={() => generateOne(a.id)}
                    disabled={gen}
                    className="h-9 bg-turquoise hover:bg-nightblue text-white rounded-none font-semibold uppercase text-[11px] tracking-widest"
                  >
                    {gen ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generando</>
                    ) : imgs.length > 0 ? (
                      <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerar</>
                    ) : (
                      <><ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Generar</>
                    )}
                  </Button>
                  <Button
                    data-testid={`social-images-shorten-${a.id}`}
                    onClick={() => shortenAndGenerate(a)}
                    disabled={gen}
                    variant="outline"
                    className="h-8 border-softblue text-nightblue hover:bg-lightgray rounded-none text-[10px] uppercase tracking-widest font-semibold"
                    title="Pedir a la IA que acorte el título antes de generar"
                  >
                    <Sparkles className="w-3 h-3 mr-1" /> IA acortar
                  </Button>
                  <Button
                    data-testid={`social-images-designer-${a.id}`}
                    onClick={() => setDesignerArticle(a)}
                    disabled={gen}
                    variant="outline"
                    className="h-8 border-softblue text-nightblue hover:bg-lightgray rounded-none text-[10px] uppercase tracking-widest font-semibold"
                    title="Abrir el diseñador manual (arrastrar y soltar)"
                  >
                    <Move className="w-3 h-3 mr-1" /> Diseñar
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview modal */}
      {previewImage && (
        <div
          data-testid="social-image-preview"
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-3xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-turquoise"
              data-testid="social-image-preview-close"
            >
              <X className="w-6 h-6" />
            </button>
            <img src={previewImage.file_url} alt="preview" className="max-w-full max-h-[80vh] object-contain shadow-2xl" />
            <div className="mt-3 flex items-center justify-between text-white text-xs">
              <span>{previewImage.width}×{previewImage.height} · {previewImage.format}</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setShareImage(previewImage); setPreviewImage(null); }}
                  className="uppercase tracking-widest font-bold text-white hover:text-turquoise"
                  data-testid="preview-share"
                >
                  Compartir
                </button>
                <button
                  onClick={() => downloadImage(previewImage)}
                  className="uppercase tracking-widest font-bold text-turquoise hover:text-white"
                >
                  Descargar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Designer modal */}
      {designerArticle && (
        <SocialImageDesigner
          article={designerArticle}
          initialTemplate={template}
          onClose={async () => {
            setDesignerArticle(null);
            // Refresh default preset (user may have marked one)
            try {
              const def = await api.adminSocialPresetDefault();
              setDefaultPreset(def?.preset || null);
            } catch { /* silent */ }
          }}
          onGenerated={(outputs) => {
            const aid = designerArticle.id;
            setImagesByArticle((prev) => ({ ...prev, [aid]: [...outputs, ...(prev[aid] || [])] }));
          }}
        />
      )}

      {/* Share modal */}
      {shareImage && (
        <SocialImageShareModal
          image={shareImage}
          onClose={() => setShareImage(null)}
        />
      )}

      {/* Publish modal (Meta) */}
      {publishTarget && (
        <SocialPublishModal
          image={publishTarget.image}
          article={publishTarget.article}
          initialPlatforms={publishTarget.platforms}
          onClose={() => setPublishTarget(null)}
          onPublished={() => { /* history refresh in Fase 3 */ }}
        />
      )}

      {/* Batch publish modal */}
      {batchPublishItems && (
        <SocialBatchPublishModal
          items={batchPublishItems}
          onClose={() => setBatchPublishItems(null)}
        />
      )}
    </div>
  );
}
